import React, { useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import './ProjectMedia.scss'

/**
 * A project's still image that turns into its silent clip while hovered, for
 * the places outside the homepage strip that show project images (/projects,
 * services, industries). The <img> keeps its own class and props, so the
 * page's existing sizing still applies; the clip sits exactly over it.
 *
 * Without `project.video`, or on touch screens and with reduced motion, it is
 * just the image.
 */
const ProjectMedia = ({ project, className = '', ...imgProps }) => {
  const videoRef = useRef(null)
  const reduced = useReducedMotion()
  const [playing, setPlaying] = useState(false)
  const hasVideo = Boolean(project.video) && !reduced

  if (!hasVideo) {
    return <img src={project.img} alt={project.title} className={className} {...imgProps} />
  }

  const start = () => {
    const video = videoRef.current
    if (!video) return
    setPlaying(true)
    const attempt = video.play()
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => setPlaying(false))
  }

  const stop = () => {
    const video = videoRef.current
    if (!video) return
    video.pause()
    video.currentTime = 0
    setPlaying(false)
  }

  return (
    <span className={`project-media${playing ? ' is-playing' : ''}`} onMouseEnter={start} onMouseLeave={stop}>
      <img src={project.img} alt={project.title} className={className} {...imgProps} />
      <video
        ref={videoRef}
        className='project-media-video'
        src={project.video}
        muted
        loop
        playsInline
        preload='none'
        aria-hidden='true'
      />
    </span>
  )
}

export default ProjectMedia
